<template>
    <div class="bg-white rounded-lg overflow-hidden">
      <div class="bg-primary text-white p-4">
        <h3 class="text-xl font-semibold">{{ isEditing ? 'Edit' : 'Create' }} Survey Template</h3>
      </div>
      <div class="p-6">
       <!-- <div class="mb-4 flex justify-end">
          <button @click="togglePreview" class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">
            Preview Template
          </button>
        </div>-->
        <form @submit.prevent="saveTemplate">
          <div class="mb-4">
            <label for="templateName" class="block text-sm font-medium text-gray-700">Template Name</label>
            <input type="text" id="templateName" v-model="templateName" required
                   class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
          </div>
          <div class="mb-4">
            <label for="templateDescription" class="block text-sm font-medium text-gray-700">Description</label>
            <textarea id="templateDescription" v-model="templateDescription" rows="3"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"></textarea>
          </div>
          <div class="mb-4">
            <h4 class="text-lg font-medium text-gray-700 mb-2">Questions</h4>
            <div v-for="(question, index) in questions" :key="index" class="mb-4 p-4 border border-gray-200 rounded-md">
              <div class="flex justify-between items-center mb-2">
                <h5 class="text-md font-medium">Question {{ index + 1 }}</h5>
                <button type="button" @click="removeQuestion(index)" class="text-red-600 hover:text-red-800">Remove</button>
              </div>
              <input v-model="question.text" type="text" placeholder="Question text" class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md">
              <!--<input v-model="question.description" type="text" placeholder="Question description (optional)" class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md">
             --> 
              <select v-model="question.type" class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="single">Single Choice</option>
                <option value="multiple">Multiple Choice</option>
                <option value="rating">Rating</option>
                <option value="text">Text</option>
              </select>
              <div v-if="question.type === 'single' || question.type === 'multiple'" class="ml-4">
                <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="flex items-center mb-2">
                  <input v-model="question.options[optionIndex]" type="text" placeholder="Option text" class="flex-grow px-3 py-2 border border-gray-300 rounded-md">
                  <button type="button" @click="removeOption(question, optionIndex)" class="ml-2 text-red-600 hover:text-red-800">Remove</button>
                </div>
                <button type="button" @click="addOption(question)" class="text-blue-600 hover:text-blue-800">Add Option</button>
              </div>
              <div v-if="question.type === 'rating'" class="ml-4">
                <label class="block mb-2">Max Rating:</label>
                <input v-model.number="question.maxRating" type="number" min="1" max="10" class="w-20 px-3 py-2 border border-gray-300 rounded-md">
              </div>
            </div>
            <div class="flex items-center mb-4">
              <button type="button" @click="addQuestion" class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 mr-2">Add Question</button>
              <div class="flex-grow">
                <input
                  v-model="questionTemplateSearchQuery"
                  type="text"
                  placeholder="Search question templates..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                <select v-model="selectedQuestionTemplate" @change="addQuestionFromTemplate" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Copy from Question Template</option>
                  <option v-for="template in filteredQuestionTemplates" :key="template.id" :value="template">
                    {{ template.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="flex justify-end space-x-2">
            <button type="button" @click="cancel" class="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition duration-300">
              Cancel
            </button>
            <button type="submit" class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300">
              {{ isEditing ? 'Update' : 'Save' }} Template
            </button>
          </div>
        </form>
      </div>
      <SurveyPreviewModal
        v-if="showPreviewModal"
        :show="showPreviewModal"
        :survey="previewTemplate"
        :availableModules="[]"
        @close="togglePreview"
      />
    </div>
    </template>
    
    <script>
    import { questionTemplateApi } from '@/api/questionTemplateApi'
    import SurveyPreviewModal from './modals/SurveyPreviewModal.vue'
    
    export default {
      name: 'SurveyTemplateBuilder',
      components: {
        SurveyPreviewModal
      },
      props: {
        template: {
          type: Object,
          required: true
        }
      },
      data() {
        return {
          id: this.template.id,
          templateName: this.template.name,
          templateDescription: this.template.description,
          questions: this.template.questions || [],
          questionTemplates: [],
          selectedQuestionTemplate: '',
          newQuestionType: 'single',
          questionTemplateSearchQuery: '',
          showPreviewModal: false,
        }
      },
      computed: {
        isEditing() {
          return this.id !== '';
        },
        filteredQuestionTemplates() {
          const query = this.questionTemplateSearchQuery.toLowerCase();
          return this.questionTemplates.filter(template => 
            template.name.toLowerCase().includes(query) ||
            template.text.toLowerCase().includes(query)
          );
        },
        previewTemplate() {
          return {
            id: this.id,
            title: this.templateName,
            description: this.templateDescription,
            type: 'Template',
            targetModule: null,
            option: '',
            department: '',
            status: 'Draft',
            startDate: null,
            endDate: null,
            questions: this.questions
          };
        }
      },
      methods: {
        addQuestion() {
          this.questions.push({
            text: '',
            description: '',
            type: this.newQuestionType,
            options: this.newQuestionType === 'single' || this.newQuestionType === 'multiple' ? [''] : [],
            maxRating: this.newQuestionType === 'rating' ? 5 : null
          })
        },
        removeQuestion(index) {
          this.questions.splice(index, 1)
        },
        addOption(question) {
          question.options.push('')
        },
        removeOption(question, index) {
          question.options.splice(index, 1)
        },
        async fetchQuestionTemplates() {
          try {
            this.questionTemplates = await questionTemplateApi.getAll();
          } catch (error) {
            console.error('Error fetching question templates:', error);
          }
        },
        addQuestionFromTemplate() {
          if (this.selectedQuestionTemplate) {
            const newQuestion = { 
              ...this.selectedQuestionTemplate,
              description: this.selectedQuestionTemplate.description || ''
            };
            this.questions.push(newQuestion);
            this.selectedQuestionTemplate = '';
          }
        },
        saveTemplate() {
          const template = {
            id: this.id,
            name: this.templateName,
            description: this.templateDescription,
            questions: this.questions
          }
          this.$emit('save', template)
        },
        cancel() {
          this.$emit('cancel')
        },
        togglePreview() {
          this.showPreviewModal = !this.showPreviewModal;
        }
      },
      mounted() {
        this.fetchQuestionTemplates();
      }
    }
    </script>
    
    